import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faLocationDot,
  faFileInvoice,
  faSignOutAlt,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../../config/api.config';
import { useLogoutHandler } from '../../utils/logoutHandler';
import LogoutConfirmDialog from '../LogoutConfirmDialog';
import logoIcon from '../../assets/images/logo-single.png';
import theme from '../../styles/theme';

interface SidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const SidebarContainer = styled.div<{ $isCollapsed: boolean; $isMobileOpen: boolean }>`
  width: ${props => (props.$isCollapsed ? '72px' : '260px')};
  background: linear-gradient(180deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 100%);
  color: white;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  padding: 24px 0;
  z-index: 1000;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 180px;
    background: radial-gradient(ellipse at 50% 0%, rgba(255, 107, 107, 0.12) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    transform: translateX(${props => (props.$isMobileOpen ? '0' : '-100%')});
    width: 260px;
    box-shadow: ${props => (props.$isMobileOpen ? '8px 0 32px rgba(0,0,0,0.3)' : 'none')};
    z-index: 1002;
  }
`;

const Logo = styled(Link)<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$isCollapsed ? 'center' : 'flex-start')};
  gap: 12px;
  padding: ${props => (props.$isCollapsed ? '0 12px' : '0 20px')};
  margin-bottom: 28px;
  text-decoration: none;
  position: relative;
  z-index: 1;
  min-height: 44px;
`;

const BrandIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  object-fit: contain;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.35);
`;

const BrandText = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #ffffff;
  white-space: nowrap;
  line-height: 1;

  span {
    background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0 12px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  position: relative;
  z-index: 1;
`;

const MenuItem = styled.li<{ $active?: boolean }>`
  border-radius: ${theme.radius.md};
  transition: background ${theme.transitions.fast};

  ${props =>
    props.$active
      ? `
    background: linear-gradient(135deg, rgba(255,107,107,0.24) 0%, rgba(255,142,83,0.14) 100%);
    box-shadow: inset 0 0 0 1px rgba(255,107,107,0.28);
  `
      : ''}

  &:hover {
    background: ${props =>
      props.$active
        ? 'linear-gradient(135deg, rgba(255,107,107,0.3) 0%, rgba(255,142,83,0.18) 100%)'
        : 'rgba(255, 255, 255, 0.07)'};
  }
`;

const MenuLink = styled(Link)<{ $isCollapsed: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 13px 16px;
  color: ${props => (props.$active ? '#fff' : 'rgba(255,255,255,0.75)')};
  text-decoration: none;
  font-size: ${theme.typography.body};
  font-weight: ${props => (props.$active ? 600 : 500)};
  border-radius: ${theme.radius.md};
  transition: all ${theme.transitions.fast};
  letter-spacing: -0.01em;

  svg {
    width: 20px;
    min-width: 20px;
    margin-right: ${props => (props.$isCollapsed ? '0' : '14px')};
    font-size: 17px;
    color: ${props => (props.$active ? theme.colors.primary : 'rgba(255,255,255,0.55)')};
    transition: color 0.2s ease;
  }

  span {
    display: ${props => (props.$isCollapsed ? 'none' : 'block')};
    white-space: nowrap;
  }

  &:hover {
    color: #fff;
    svg {
      color: ${theme.colors.primary};
    }
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 14px;
    }
    span {
      display: block;
    }
  }
`;

const LogoutButton = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: calc(100% - 24px);
  margin: 8px 12px 0;
  padding: 13px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${theme.radius.md};
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;

  svg {
    width: 20px;
    min-width: 20px;
    margin-right: ${props => (props.$isCollapsed ? '0' : '14px')};
  }

  span {
    display: ${props => (props.$isCollapsed ? 'none' : 'block')};
  }

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.3);
    color: #fca5a5;
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 14px;
    }
    span {
      display: block;
    }
  }
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
    z-index: 1001;
    opacity: ${props => (props.$isVisible ? 1 : 0)};
    visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
    transition: all 0.3s ease;
    backdrop-filter: blur(4px);
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, isMobileOpen, onMobileClose }) => {
  const location = useLocation();

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel,
  } = useLogoutHandler();

  const navItems = [
    { path: APP_ROUTES.DASHBOARD, icon: faHome, label: 'Dashboard' },
    { path: APP_ROUTES.SUBSCRIBED_LOCATIONS, icon: faLocationDot, label: 'Subscribed locations' },
    { path: APP_ROUTES.INVOICE_LIST, icon: faFileInvoice, label: 'Invoice list' },
    { path: APP_ROUTES.FAQ, icon: faQuestionCircle, label: 'FAQ' },
  ];

  return (
    <>
      <Overlay $isVisible={isMobileOpen} onClick={onMobileClose} />

      <SidebarContainer $isCollapsed={isCollapsed} $isMobileOpen={isMobileOpen}>
        <Logo to={APP_ROUTES.DASHBOARD} $isCollapsed={isCollapsed}>
          <BrandIcon src={logoIcon} alt="WhatzPro" />
          {!isCollapsed && (
            <BrandText>
              What<span>z</span>Pro
            </BrandText>
          )}
        </Logo>

        <MenuList>
          {navItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <MenuItem key={item.path} $active={active}>
                <MenuLink to={item.path} $isCollapsed={isCollapsed} $active={active}>
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                </MenuLink>
              </MenuItem>
            );
          })}
        </MenuList>

        <LogoutButton onClick={handleLogoutClick} $isCollapsed={isCollapsed}>
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Logout</span>
        </LogoutButton>
      </SidebarContainer>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </>
  );
};

export default Sidebar;
